import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Table,
  Tag,
  Switch,
  Form,
  Input,
  InputNumber,
  Radio,
  DatePicker,
  Popconfirm,
  message,
  Space,
  Tooltip,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Badge,
  Empty,
  Avatar,
  Divider,
} from "antd";
import {
  TagOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  TeamOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createCoupon,
  getCouponsByCourseId,
  getCouponRedemptions,
  updateCouponStatus,
  deleteCoupon,
} from "../../../../../Constants/Api/functions/function";
import { DEFAULT_USER_PROFILE_IMAGE } from "../../../../../Constants/Api/azure/azure";
import "./styles.scss";

const { Text } = Typography;

const CourseCoupons = ({
  open,
  onClose,
  courseId,
  course,
  userProfile,
}) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Redemption history modal state
  const [redemptionsModalOpen, setRedemptionsModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [redemptionSearch, setRedemptionSearch] = useState("");

  const [form] = Form.useForm();
  const [discountTypeWatch, setDiscountTypeWatch] = useState("percentage");
  const [accessTypeWatch, setAccessTypeWatch] = useState("unlimited");

  // Fetch coupons for this course
  const fetchCoupons = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await getCouponsByCourseId(courseId);
      if (res && res.success) {
        setCoupons(res.coupons || []);
      } else {
        setCoupons([]);
      }
    } catch (error) {
      console.error("fetchCoupons error:", error);
      message.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (open && courseId) {
      fetchCoupons();
    }
  }, [open, courseId, fetchCoupons]);

  // Generate random promo code
  const handleGenerateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let randomPart = "";
    for (let i = 0; i < 6; i++) {
      randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const prefix = course?.name ? course.name.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, "DA") : "DA";
    form.setFieldsValue({ code: `${prefix}-${randomPart}` });
  };

  // Create new coupon
  const handleCreateCoupon = async (values) => {
    setIsSubmitting(true);
    try {
      const isUnlimited = values.accessType === "unlimited";
      const payload = {
        courseId,
        code: values.code.trim().toUpperCase(),
        discountType: values.discountType,
        discountValue: Number(values.discountValue),
        isUnlimited,
        maxUses: isUnlimited ? null : Number(values.maxUses),
        expiryDate: values.expiryDate ? values.expiryDate.toISOString() : null,
        createdBy: userProfile?.id || "system",
      };

      const res = await createCoupon(payload);
      if (res && res.success) {
        message.success(`Coupon "${payload.code}" created successfully!`);
        form.resetFields();
        setCreateModalOpen(false);
        fetchCoupons();
      } else {
        message.error(res?.message || "Failed to create coupon");
      }
    } catch (error) {
      console.error("handleCreateCoupon error:", error);
      message.error("Failed to create coupon");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle active / inactive status
  const handleToggleStatus = async (coupon, checked) => {
    const newStatus = checked ? "active" : "inactive";
    try {
      const res = await updateCouponStatus(coupon.id, newStatus);
      if (res && res.success) {
        message.success(`Coupon marked as ${newStatus}`);
        setCoupons((prev) =>
          prev.map((c) => (c.id === coupon.id ? { ...c, status: newStatus } : c))
        );
      } else {
        message.error(res?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("handleToggleStatus error:", error);
      message.error("Failed to update status");
    }
  };

  // Delete coupon
  const handleDeleteCoupon = async (couponId) => {
    try {
      const res = await deleteCoupon(couponId);
      if (res && res.success) {
        message.success("Coupon deleted successfully");
        fetchCoupons();
      } else {
        message.error(res?.message || "Failed to delete coupon");
      }
    } catch (error) {
      console.error("handleDeleteCoupon error:", error);
      message.error("Failed to delete coupon");
    }
  };

  // Open redeemed users modal
  const handleViewRedemptions = async (coupon) => {
    setSelectedCoupon(coupon);
    setRedemptionsModalOpen(true);
    setLoadingRedemptions(true);
    setRedemptionSearch("");
    try {
      const res = await getCouponRedemptions(coupon.id);
      if (res && res.success) {
        setRedemptions(res.redemptions || []);
      } else {
        setRedemptions([]);
      }
    } catch (error) {
      console.error("handleViewRedemptions error:", error);
      message.error("Failed to load redeemed users");
    } finally {
      setLoadingRedemptions(false);
    }
  };

  // Copy code helper
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    message.success(`Copied "${code}" to clipboard!`);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Filter redemptions by search text
  const filteredRedemptions = redemptions.filter((r) => {
    if (!redemptionSearch) return true;
    const term = redemptionSearch.toLowerCase();
    return (
      (r.userName && r.userName.toLowerCase().includes(term)) ||
      (r.userEmail && r.userEmail.toLowerCase().includes(term)) ||
      (r.userMobile && r.userMobile.toLowerCase().includes(term)) ||
      (r.userIndexNumber && String(r.userIndexNumber).includes(term))
    );
  });

  // Calculate statistics
  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter((c) => c.status === "active").length;
  const totalRedemptionsCount = coupons.reduce(
    (sum, c) => sum + (Number(c.totalRedemptions) || Number(c.usedCount) || 0),
    0
  );

  const columns = [
    {
      title: "Coupon Code",
      dataIndex: "code",
      key: "code",
      render: (code) => (
        <Space>
          <Tag color="cyan" className="coupon-code-badge">
            <TagOutlined style={{ marginRight: 6 }} />
            {code}
          </Tag>
          <Tooltip title="Copy Code">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCopyCode(code)}
              className={copiedCode === code ? "copied" : ""}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => {
        const isPercentage = record.discountType === "percentage";
        const val = Number(record.discountValue);
        const coursePrice = Number(course?.price) || 0;
        let approximateSavings = 0;
        if (isPercentage) {
          approximateSavings = (coursePrice * val) / 100;
        } else {
          approximateSavings = Math.min(coursePrice, val);
        }

        return (
          <div className="discount-cell">
            {isPercentage && val === 100 ? (
              <Tag color="gold" className="discount-tag">
                <GiftOutlined /> 100% FREE
              </Tag>
            ) : isPercentage ? (
              <Tag color="green" className="discount-tag">
                {val}% OFF
              </Tag>
            ) : (
              <Tag color="blue" className="discount-tag">
                LKR {val.toLocaleString()} OFF
              </Tag>
            )}
            {coursePrice > 0 && (
              <div className="savings-preview">
                Save LKR {approximateSavings.toLocaleString()}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Access Limit",
      key: "accessLimit",
      render: (_, record) => {
        const isUnlimited = !!record.isUnlimited;
        const used = Number(record.totalRedemptions) || Number(record.usedCount) || 0;
        const max = Number(record.maxUses) || 0;
        const isExhausted = !isUnlimited && used >= max;

        if (isUnlimited) {
          return (
            <div>
              <Tag color="purple">Unlimited</Tag>
              <div className="used-subtext">{used} used so far</div>
            </div>
          );
        }

        return (
          <div>
            <Tag color={isExhausted ? "error" : "geekblue"}>
              {used} / {max} Users
            </Tag>
            {isExhausted && (
              <div className="exhausted-subtext">Limit Reached</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Redeemed Users",
      key: "redeemedUsers",
      render: (_, record) => {
        const count = Number(record.totalRedemptions) || Number(record.usedCount) || 0;
        return (
          <Button
            type={count > 0 ? "primary" : "default"}
            ghost={count > 0}
            size="small"
            icon={<TeamOutlined />}
            onClick={() => handleViewRedemptions(record)}
            className="view-users-btn"
            style={
              count > 0
                ? { borderColor: "#1677ff", color: "#1677ff", fontWeight: 600 }
                : { color: "#8c8c8c" }
            }
          >
            <span>{count > 0 ? `View ${count} User${count > 1 ? "s" : ""}` : "0 Redeemed"}</span>
          </Button>
        );
      },
    },
    {
      title: "Expiry",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (expiryDate) => {
        if (!expiryDate) {
          return <Text type="secondary">No Expiry</Text>;
        }
        const isExpired = dayjs().isAfter(dayjs(expiryDate));
        return (
          <div>
            <div>{dayjs(expiryDate).format("MMM DD, YYYY")}</div>
            {isExpired ? (
              <Tag color="red">Expired</Tag>
            ) : (
              <span className="valid-subtext">Active</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          checked={record.status === "active"}
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Redeemed Users">
            <Button
              type="text"
              icon={<EyeOutlined style={{ color: "#1677ff", fontSize: 16 }} />}
              onClick={() => handleViewRedemptions(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Coupon"
            description={`Are you sure you want to delete coupon "${record.code}"?`}
            onConfirm={() => handleDeleteCoupon(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Coupon">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div className="course-coupons-modal-header">
            <div className="title-area">
              <span className="icon-wrapper">
                <TagOutlined />
              </span>
              <div>
                <h3>Course Coupons</h3>
                <p className="course-name-subtitle">
                  {course?.name ? `Course: ${course.name}` : "Manage Discounts & Access"}
                </p>
              </div>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({
                  discountType: "percentage",
                  accessType: "unlimited",
                });
                setDiscountTypeWatch("percentage");
                setAccessTypeWatch("unlimited");
                handleGenerateCode();
                setCreateModalOpen(true);
              }}
              className="create-coupon-btn"
            >
              Issue Coupon
            </Button>
          </div>
        }
        open={open}
        onCancel={onClose}
        width={980}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        className="course-coupons-modal"
      >
        {/* Statistics Bar */}
        <Row gutter={16} className="coupon-stats-row">
          <Col span={8}>
            <Card className="stat-card">
              <Statistic
                title="Total Coupons"
                value={totalCoupons}
                prefix={<TagOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card className="stat-card">
              <Statistic
                title="Active Coupons"
                value={activeCoupons}
                valueStyle={{ color: "#3f8600" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card className="stat-card">
              <Statistic
                title="Total Redemptions"
                value={totalRedemptionsCount}
                valueStyle={{ color: "#1677ff" }}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Coupons Table */}
        <div className="coupons-table-wrapper">
          <Table
            columns={columns}
            dataSource={coupons}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 6 }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <span>
                      No coupons created yet for this course. Click{" "}
                      <strong>"Issue Coupon"</strong> to offer discounts!
                    </span>
                  }
                />
              ),
            }}
          />
        </div>
      </Modal>

      {/* CREATE COUPON MODAL */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <TagOutlined style={{ color: "#1677ff", fontSize: 20 }} />
            <span>Create New Course Coupon</span>
          </div>
        }
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        destroyOnClose
        width={600}
        className="create-coupon-dialog"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateCoupon}
          initialValues={{
            discountType: "percentage",
            accessType: "unlimited",
            discountValue: 20,
          }}
        >
          {/* Coupon Code */}
          <Form.Item
            label="Coupon Code"
            name="code"
            rules={[
              { required: true, message: "Please enter or generate a coupon code" },
              {
                pattern: /^[A-Za-z0-9_-]+$/,
                message: "Only letters, numbers, hyphens, and underscores are allowed",
              },
            ]}
            extra="Students will type this code during checkout."
          >
            <Input
              placeholder="e.g. SUMMER50 or FREEPASS"
              size="large"
              style={{ textTransform: "uppercase" }}
              addonAfter={
                <Button
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={handleGenerateCode}
                  style={{ padding: 0 }}
                >
                  Generate
                </Button>
              }
            />
          </Form.Item>

          <Row gutter={16}>
            {/* Discount Type */}
            <Col span={12}>
              <Form.Item
                label="Discount Type"
                name="discountType"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  buttonStyle="solid"
                  style={{ width: "100%", display: "flex" }}
                  onChange={(e) => setDiscountTypeWatch(e.target.value)}
                >
                  <Radio.Button value="percentage" style={{ flex: 1, textAlign: "center" }}>
                    Percentage (%)
                  </Radio.Button>
                  <Radio.Button value="fixed" style={{ flex: 1, textAlign: "center" }}>
                    Fixed (LKR)
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>

            {/* Discount Value */}
            <Col span={12}>
              <Form.Item
                label={
                  discountTypeWatch === "percentage"
                    ? "Discount Percentage (%)"
                    : "Discount Amount (LKR)"
                }
                name="discountValue"
                rules={[
                  { required: true, message: "Please enter discount value" },
                  {
                    validator: (_, value) => {
                      if (discountTypeWatch === "percentage") {
                        if (value < 1 || value > 100) {
                          return Promise.reject(new Error("Percentage must be between 1% and 100%"));
                        }
                      } else {
                        if (value <= 0) {
                          return Promise.reject(new Error("Discount amount must be greater than 0"));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  discountTypeWatch === "percentage"
                    ? "Tip: 100% gives completely free enrollment"
                    : `Course price: LKR ${course?.price || 0}`
                }
              >
                <InputNumber
                  min={1}
                  max={discountTypeWatch === "percentage" ? 100 : undefined}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder={discountTypeWatch === "percentage" ? "e.g. 20 for 20%" : "e.g. 1500"}
                  addonAfter={discountTypeWatch === "percentage" ? "%" : "LKR"}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }} />

          {/* Access / Limit */}
          <Form.Item
            label="Access Availability"
            name="accessType"
            rules={[{ required: true }]}
            extra="Specify whether all eligible students can use it, or cap the number of redemptions."
          >
            <Radio.Group
              onChange={(e) => setAccessTypeWatch(e.target.value)}
              style={{ width: "100%" }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value="unlimited">
                  <strong>Unlimited</strong> — Any student can redeem this coupon
                </Radio>
                <Radio value="limited">
                  <strong>Limited Users</strong> — Restrict to a specific number of students
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {accessTypeWatch === "limited" && (
            <Form.Item
              label="Maximum Allowed Users"
              name="maxUses"
              rules={[
                { required: true, message: "Please specify how many users can access" },
                {
                  type: "number",
                  min: 1,
                  message: "Must allow at least 1 user",
                },
              ]}
              extra="Once this number of students enroll with this coupon, it will expire automatically."
            >
              <InputNumber
                min={1}
                style={{ width: "100%" }}
                size="large"
                placeholder="e.g. 50 students"
                addonAfter="Students"
              />
            </Form.Item>
          )}

          {/* Expiry Date (Optional) */}
          <Form.Item
            label="Expiration Date (Optional)"
            name="expiryDate"
            extra="Leave empty if you want this coupon to remain valid indefinitely."
          >
            <DatePicker
              style={{ width: "100%" }}
              size="large"
              placeholder="Select expiration date"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Issue Coupon
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* IDENTIFY USERS WHO USED THE COUPON MODAL */}
      <Modal
        title={
          <div className="redemptions-modal-header">
            <SafetyCertificateOutlined style={{ color: "#52c41a", fontSize: 22 }} />
            <div>
              <h3>Redeemed Students</h3>
              <p>
                Coupon: <strong>{selectedCoupon?.code}</strong> &nbsp;•&nbsp;
                {selectedCoupon?.discountType === "percentage"
                  ? `${selectedCoupon?.discountValue}% OFF`
                  : `LKR ${selectedCoupon?.discountValue} OFF`}
                &nbsp;•&nbsp; Total Users:{" "}
                <strong>{redemptions.length}</strong>
              </p>
            </div>
          </div>
        }
        open={redemptionsModalOpen}
        onCancel={() => setRedemptionsModalOpen(false)}
        width={850}
        footer={[
          <Button key="close" onClick={() => setRedemptionsModalOpen(false)}>
            Close
          </Button>,
        ]}
        className="redemptions-history-modal"
      >
        <div className="redemption-search-bar">
          <Input
            placeholder="Search student name, email, or index number..."
            prefix={<SearchOutlined />}
            value={redemptionSearch}
            onChange={(e) => setRedemptionSearch(e.target.value)}
            allowClear
            style={{ width: 340, marginBottom: 16 }}
          />
        </div>

        <Table
          dataSource={filteredRedemptions}
          rowKey="redemptionId"
          loading={loadingRedemptions}
          pagination={{ pageSize: 6 }}
          locale={{
            emptyText: (
              <Empty
                description="No students have redeemed this coupon yet."
              />
            ),
          }}
          columns={[
            {
              title: "Student",
              key: "user",
              render: (_, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar
                    src={record.userProfileImage || DEFAULT_USER_PROFILE_IMAGE}
                    icon={<UserOutlined />}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {record.userName || "Unknown"}
                    </div>
                    {record.userIndexNumber && (
                      <Tag color="geekblue" style={{ fontSize: 11, padding: "0 4px" }}>
                        #{record.userIndexNumber}
                      </Tag>
                    )}
                  </div>
                </div>
              ),
            },
            {
              title: "Contact Info",
              key: "contact",
              render: (_, record) => (
                <div style={{ fontSize: 12 }}>
                  <div>{record.userEmail || "No Email"}</div>
                  {record.userMobile && (
                    <div style={{ color: "#8c8c8c" }}>{record.userMobile}</div>
                  )}
                </div>
              ),
            },
            {
              title: "Redeemed On",
              dataIndex: "redeemedAt",
              key: "redeemedAt",
              render: (redeemedAt) => (
                <div style={{ fontSize: 12 }}>
                  <div>{dayjs(redeemedAt).format("MMM DD, YYYY")}</div>
                  <div style={{ color: "#8c8c8c" }}>
                    {dayjs(redeemedAt).format("hh:mm A")}
                  </div>
                </div>
              ),
            },
            {
              title: "Payment Summary",
              key: "paymentSummary",
              render: (_, record) => {
                const discount = Number(record.discountAmount) || 0;
                const final = Number(record.finalAmount) || 0;
                return (
                  <div>
                    <div>
                      <Tag color="green">
                        - LKR {discount.toLocaleString()}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 12, color: "#595959", marginTop: 4 }}>
                      Paid: <strong>LKR {final.toLocaleString()}</strong>
                    </div>
                  </div>
                );
              },
            },
            {
              title: "Method",
              key: "method",
              render: (_, record) => {
                const method = record.paymentMethod || "Coupon Direct";
                const isCompleted =
                  record.paymentStatus === "completed" ||
                  record.paymentStatus === "paid" ||
                  !record.paymentStatus;
                return (
                  <div>
                    <Tag color="cyan">{method}</Tag>
                    <div>
                      <Tag color={isCompleted ? "success" : "processing"} style={{ fontSize: 10, marginTop: 4 }}>
                        {isCompleted ? "Verified" : "Pending"}
                      </Tag>
                    </div>
                  </div>
                );
              },
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default CourseCoupons;
